/*
 * useTileData · Governance Tile · Live Session hook.
 *
 * Phase 3 wiring (IJ-083 · 2026-07-24) · fetches the live summary from
 * GET /api/admin/governance-tile/summary (SPD v5.91 endpoint) and
 * exposes it to every column renderer.  Every metric that the endpoint
 * does NOT yet compute is exposed as `null` so the renderer can show
 * an em dash (pending state) instead of a fabricated integer.
 *
 * IJ-122 (2026-07-28) · scope-aware fetch.  Endpoint accepts a
 * `scope=` query parameter (session · today · weekly · monthly ·
 * yearly).  The hook re-fetches when the view toggles between the
 * four tabs, so counters truly change scope rather than always
 * showing today (UTC calendar day).  Session-scope anchors on the
 * latest CHANGELOG session-open attestation (LESSON-021).
 *
 * Contract for downstream phases:
 *   view              · 'session' | 'weekly' | 'monthly' | 'yearly'
 *   setView(next)     · updates the toggle · triggers scoped re-fetch
 *   zeroCounters      · [artefacts_public_without_approval,
 *                        unauth_endpoints_reachable] — both must be 0
 *                       or the Sev-0 signal fires (IJ-079 · pending
 *                       wiring · stubbed at [0, 0] until the backend
 *                       counters land).
 *   todayISO          · wall-clock date for the session preface
 *   summary           · raw response from /admin/governance-tile/summary
 *                       or null while loading / on error
 *   loading           · true while any fetch is in flight
 *   error             · axios error object if the fetch failed, else null
 */
import { useEffect, useState } from 'react';
import authedAxios from '../../../lib/authedAxios';

const VALID_VIEWS = ['session', 'weekly', 'monthly', 'yearly'];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Module-level per-scope cache.  Prevents React 18 StrictMode double-
// mount from firing the same scoped fetch twice.  Each scope caches
// independently so tab switches do not re-hit the network once every
// scope has been fetched at least once.
const _cache = {}; // scope -> { data, error }
const _inFlight = {}; // scope -> Promise

function fetchSummaryDeduped(scope) {
  if (_cache[scope]) {
    return Promise.resolve(_cache[scope]);
  }
  if (_inFlight[scope]) return _inFlight[scope];
  _inFlight[scope] = authedAxios
    .get(`${BACKEND_URL}/api/admin/governance-tile/summary?scope=${scope}`)
    .then((r) => {
      _cache[scope] = { data: r.data, error: null };
      delete _inFlight[scope];
      return _cache[scope];
    })
    .catch((err) => {
      _cache[scope] = { data: null, error: err };
      delete _inFlight[scope];
      return _cache[scope];
    });
  return _inFlight[scope];
}

export default function useTileData() {
  const [view, setViewInternal] = useState('session');
  const [summary, setSummary] = useState(_cache['session']?.data ?? null);
  const [loading, setLoading] = useState(!_cache['session']);
  const [error, setError] = useState(_cache['session']?.error ?? null);

  const setView = (next) => {
    if (VALID_VIEWS.includes(next)) setViewInternal(next);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(!_cache[view]);
    fetchSummaryDeduped(view).then(({ data, error: err }) => {
      if (cancelled) return;
      setSummary(data);
      setError(err);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [view]);

  const todayISO = new Date().toISOString().slice(0, 10);

  return {
    view,
    setView,
    zeroCounters: [0, 0],
    todayISO,
    summary,
    loading,
    error,
  };
}
